import { CartBody, CartItemBody, CartResponse, CartVoucherBody } from "../../sdk/types"
import { eeAddToCart, eeRemovalFromCart } from "../../utils/analytics"
import { useAPIState, useSDK } from "../../context/client"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { APIError } from "../../sdk/api"
import { checkAddress } from "../../utils/check-address"
import { errorToast } from "../../utils/error-toast"
import { useEffect } from "react"
import { useLocalesState } from "../../context/locales"
import { useQueryCustomer } from "../customer/use-query-customer"

const useQueryCart = (options?: { background?: boolean; enabled?: boolean }) => {
    const queryClient = useQueryClient()
    const { language } = useLocalesState()
    const client = useSDK()
    const cart_id = useAPIState(s => s.cart_id)
    const cart_access_token = useAPIState(s => s.cart_access_token)

    const query = useQuery({
        queryKey: ["getCart", { id: cart_id, language }],
        queryFn: ({ signal }) =>
            client.getCart(cart_id as string, { access_token: cart_access_token }, language, signal),
        enabled: !!(cart_id && cart_access_token && (options?.enabled ?? true)),
        meta: { background: options?.background },
    })

    useEffect(() => {
        const error = query.error
        if (!error || error.code !== "products_unavailable") return

        queryClient.setQueriesData<CartResponse>({ queryKey: ["getCart", { id: cart_id }] }, oldData => {
            if (!oldData) {
                client.clearCart()
                return
            }

            errorToast(error)
            return {
                ...oldData,
                items: oldData.items.map(i => {
                    const errorItem = (error.detail as { line_id: string }[]).find(d => d.line_id === i.id)
                    if (errorItem) {
                        return {
                            ...i,
                            error: {
                                code: error.code,
                            },
                        }
                    }
                    return i
                }),
            }
        })
    }, [query.error, cart_id, client, queryClient, language])

    return query
}

const useQueryHistoricCart = () => {
    const { language } = useLocalesState()
    const client = useSDK()
    const historic_cart_id = useAPIState(s => s.historic_cart_id)
    const historic_cart_access_token = useAPIState(s => s.historic_cart_access_token)

    return useQuery({
        queryKey: ["getCart", { id: historic_cart_id, language }],
        queryFn: ({ signal }) =>
            client.getCart(historic_cart_id as string, { access_token: historic_cart_access_token }, language, signal),
        enabled: !!(historic_cart_id && historic_cart_access_token),
        meta: { errorToast: true },
    })
}

const usePostCart = () => {
    const queryClient = useQueryClient()
    const { region, language } = useLocalesState()
    const client = useSDK()

    const { data: customerData } = useQueryCustomer()

    return useMutation({
        mutationKey: ["mutateCart"],
        mutationFn: (body: Omit<CartBody, "session_country">) => {
            const defaultBillingAddress = customerData?.addresses.find(a => a.default_billing) || customerData?.address
            const defaultShippingAddress =
                customerData?.addresses.find(a => a.default_shipping) || customerData?.address
            const bodyWithAddresses = {
                ...body,
                session_country: defaultShippingAddress?.country || region,
            }
            if (checkAddress(defaultBillingAddress)) {
                bodyWithAddresses.addresses = {
                    invoice: {
                        id: Number(defaultBillingAddress?.id),
                        access_token: defaultBillingAddress?.access_token,
                    },
                }
            }
            if (checkAddress(defaultShippingAddress)) {
                bodyWithAddresses.addresses = {
                    ...bodyWithAddresses.addresses,
                    shipping: {
                        id: Number(defaultShippingAddress?.id),
                        access_token: defaultShippingAddress?.access_token,
                    },
                }
            }

            return client.createCart(bodyWithAddresses, language)
        },
        onSuccess: data => {
            data.items.forEach(i => {
                eeAddToCart(i, i.quantity, i.price.currency.name, data)
            })
            queryClient.setQueryData(["getCart", { id: data.id, language }], data)
        },
        retry: (failureCount, error) => {
            return error.type !== "ValidationError" &&
                error.type !== "AccessError" &&
                failureCount <= 2 &&
                error.status !== 404
                ? true
                : false
        },
        retryDelay: attempt => Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000),
    })
}

const useMutationCart = () => {
    const queryClient = useQueryClient()
    const { language } = useLocalesState()
    const client = useSDK()
    const cart_id = useAPIState(s => s.cart_id)
    const cart_access_token = useAPIState(s => s.cart_access_token)

    const { data: cartData } = useQueryCart()
    const createCart = usePostCart()

    const bundleInCart = cartData?.items.some(i => i.product.category.type === "bundle")

    const validateCart = (subscription: boolean) => {
        if (!subscription) return

        if (subscription && bundleInCart) {
            throw new APIError("illegal_combination", { code: "illegal_combination_subscription" })
        }
    }

    return useMutation({
        mutationKey: ["mutateCart", { id: cart_id }],
        mutationFn: async (body: CartBody) => {
            validateCart(!!body.subscription)

            try {
                return await client.updateCart(
                    cart_id as string,
                    body,
                    {
                        access_token: cart_access_token,
                    },
                    language
                )
            } catch (err) {
                const error = err as APIError
                if (error.code !== "989e2d90" && error.code !== "64fb4138" && error.code !== "b3101604") throw err

                queryClient.removeQueries({ queryKey: ["getCart", { id: cart_id }] })
                client.clearCart()
                return await createCart.mutateAsync(body)
            }
        },
        onSuccess: data => {
            const prevData = queryClient.getQueryData<CartResponse>(["getCart", { id: cart_id, language }])
            queryClient.setQueryData(["getCart", { id: cart_id, language }], data)
            if (
                prevData?.addresses.invoice?.id !== data.addresses.invoice?.id ||
                prevData?.price.total !== data.price.total
            ) {
                queryClient.invalidateQueries({ queryKey: ["getCartFinanceProviders"] })
            }
            if (prevData?.addresses.shipping?.id !== data.addresses.shipping?.id) {
                queryClient.invalidateQueries({ queryKey: ["getCartShippingCarriers"] })
            }
        },
        retry: (failureCount, error) => {
            return error.type !== "ValidationError" &&
                error.type !== "AccessError" &&
                error.type !== "UserError" &&
                error.type !== "MissingError" &&
                error.message !== "illegal_combination" &&
                failureCount <= 2
                ? true
                : false
        },
        retryDelay: attempt => Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000),
    })
}

const usePostCartLine = () => {
    const queryClient = useQueryClient()
    const { language } = useLocalesState()
    const client = useSDK()
    const cart_id = useAPIState(s => s.cart_id)
    const cart_access_token = useAPIState(s => s.cart_access_token)

    const { data: cartData } = useQueryCart()
    const createCart = usePostCart()

    const subscriptionInCart = cartData?.items.some(i => i.product.category.type === "subscription")
    const giftInCart = cartData?.items.some(i => i.product.category.type === "gift")

    const validateCart = (category: string) => {
        if (!category) return

        if (category === "bundle" && subscriptionInCart) {
            throw new APIError("illegal_combination", { code: "illegal_combination_bundle" })
        } else if (
            (category !== "gift" && giftInCart) ||
            (category === "gift" && !giftInCart && (cartData?.items.reduce((a, b) => a + b.quantity, 0) || 0) !== 0)
        ) {
            throw new APIError("illegal_combination", { code: "illegal_combination_gift" })
        }
    }

    return useMutation({
        mutationKey: ["mutateCart", { id: cart_id }],
        mutationFn: async (body: CartItemBody & { category?: string }) => {
            const { category, ...restBody } = body

            if (category) validateCart(category)

            try {
                return await client.createCartLine(
                    cart_id as string,
                    restBody,
                    { access_token: cart_access_token },
                    language
                )
            } catch (err) {
                const error = err as APIError
                if (error.code !== "989e2d90" && error.code !== "b3101604") throw err

                queryClient.removeQueries({ queryKey: ["getCart", { id: cart_id }] })
                client.clearCart()
                return await createCart.mutateAsync({ items: [restBody.item] })
            }
        },
        onSuccess: data => {
            const prevData = queryClient.getQueryData<CartResponse>(["getCart", { id: cart_id, language }])
            data.items.forEach(i => {
                const prevProduct = prevData?.items.find(prevItem => prevItem.id === i.id)
                if (!prevProduct) return eeAddToCart(i, i.quantity, i.price.currency.name, data)
            })
            queryClient.setQueryData(["getCart", { id: cart_id, language }], data)
            if (prevData?.price.total !== data.price.total) {
                queryClient.invalidateQueries({ queryKey: ["getCartFinanceProviders"] })
            }
            queryClient.invalidateQueries({ queryKey: ["getCartShippingCarriers"] })
        },
        retry: (failureCount, error) => {
            return error.type !== "ValidationError" &&
                error.type !== "AccessError" &&
                error.type !== "UserError" &&
                error.type !== "MissingError" &&
                error.message !== "illegal_combination" &&
                failureCount <= 2
                ? true
                : false
        },
        retryDelay: attempt => Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000),
    })
}

const useMutationCartLine = () => {
    const queryClient = useQueryClient()
    const { language } = useLocalesState()
    const client = useSDK()
    const cart_id = useAPIState(s => s.cart_id)
    const cart_access_token = useAPIState(s => s.cart_access_token)
    const createCart = usePostCart()

    return useMutation({
        mutationKey: ["mutateCart", { id: cart_id }],
        mutationFn: async ({ id, body }: { id: string; body: CartItemBody & { category?: string } }) => {
            const { category: _category, ...restBody } = body

            try {
                return await client.updateCartLine(
                    cart_id as string,
                    id,
                    restBody,
                    { access_token: cart_access_token },
                    language
                )
            } catch (err) {
                const error = err as APIError
                if (error.code !== "989e2d90" && error.code !== "b3101604") throw err

                queryClient.removeQueries({ queryKey: ["getCart", { id: cart_id }] })
                client.clearCart()
                return await createCart.mutateAsync({ items: [restBody.item] })
            }
        },
        onSuccess: data => {
            const prevData = queryClient.getQueryData<CartResponse>(["getCart", { id: cart_id, language }])
            data.items.forEach(i => {
                const prevProduct = prevData?.items.find(prevItem => prevItem.id === i.id)
                if (!prevProduct) return eeAddToCart(i, i.quantity, i.price.currency.name, data)

                const quantityDif = i.quantity - prevProduct.quantity
                if (quantityDif < 0) {
                    eeRemovalFromCart(i, -quantityDif, i.price.currency.name, data)
                } else if (quantityDif > 0) {
                    eeAddToCart(i, quantityDif, i.price.currency.name, data)
                }
            })
            if (!prevData) return
            prevData.items.forEach(prevItem => {
                const product = data.items.find(i => i.id === prevItem.id)
                if (product) return
                eeRemovalFromCart(prevItem, prevItem.quantity, prevItem.price.currency.name, data)
            })
            queryClient.setQueryData(["getCart", { id: cart_id, language }], data)
            if (prevData.price.total !== data.price.total) {
                queryClient.invalidateQueries({ queryKey: ["getCartFinanceProviders"] })
            }
        },
        retry: (failureCount, error) => {
            return error.type !== "ValidationError" &&
                error.type !== "AccessError" &&
                error.type !== "UserError" &&
                error.type !== "MissingError" &&
                failureCount <= 2
                ? true
                : false
        },
        retryDelay: attempt => Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000),
    })
}

const useDeleteCartLine = () => {
    const queryClient = useQueryClient()
    const { language } = useLocalesState()
    const client = useSDK()
    const cart_id = useAPIState(s => s.cart_id)
    const cart_access_token = useAPIState(s => s.cart_access_token)

    return useMutation({
        mutationKey: ["mutateCart", { id: cart_id }],
        mutationFn: async ({ id }: { id: string }) => {
            try {
                return await client.deleteCartLine(cart_id as string, id, { access_token: cart_access_token }, language)
            } catch (err) {
                const error = err as APIError
                if (error.code !== "64fb4138" && error.code !== "b3101604") throw err

                queryClient.removeQueries({ queryKey: ["getCart", { id: cart_id }] })
                client.clearCart()
            }
        },
        onSuccess: (_, variables) => {
            const prevData = queryClient.getQueryData<CartResponse>(["getCart", { id: cart_id, language }])
            if (!prevData) return
            const prevItem = prevData.items.find(i => i.id === variables.id)
            if (!prevItem) return

            const newData = {
                ...prevData,
                items: prevData.items.filter(i => i.id !== variables.id),
                price: {
                    ...prevData.price,
                    total: prevData.price.total - prevItem.price.amount_taxed,
                    pay_now:
                        prevData.price.pay_now -
                        (prevItem.splits?.length ? prevItem.splits[0].price : prevItem.price.amount_taxed),
                },
            }
            eeRemovalFromCart(prevItem, prevItem.quantity, prevItem.price.currency.name, newData)
            queryClient.setQueryData(["getCart", { id: cart_id, language }], newData)
            queryClient.invalidateQueries({ queryKey: ["getCart", { id: cart_id }] })
            if (prevData.price.total !== newData.price.total) {
                queryClient.invalidateQueries({ queryKey: ["getCartFinanceProviders"] })
            }
            queryClient.invalidateQueries({ queryKey: ["getCartShippingCarriers"] })
        },
        retry: (failureCount, error) => {
            return error.type !== "ValidationError" &&
                error.type !== "AccessError" &&
                error.type !== "UserError" &&
                error.type !== "MissingError" &&
                failureCount <= 2
                ? true
                : false
        },
        retryDelay: attempt => Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000),
    })
}

const useCartLine = (sku: string) => {
    const cart_id = useAPIState(s => s.cart_id)
    const cart_access_token = useAPIState(s => s.cart_access_token)

    const { data: cartData } = useQueryCart()

    const createCart = usePostCart()
    const createCartLine = usePostCartLine()
    const updateCartLine = useMutationCartLine()
    const hasCart = !!(cart_id && cart_access_token)
    const item = cartData?.items.find(i => i.product.sku === sku)

    return useMutation({
        mutationKey: ["mutateCart", { id: cart_id }],
        mutationFn: async (body: CartItemBody) => {
            if (!hasCart) {
                return await createCart.mutateAsync({ items: [body.item] })
            }

            if (item) {
                return await updateCartLine.mutateAsync({ id: item.id, body })
            }

            return await createCartLine.mutateAsync(body)
        },
    })
}

const useCart = () => {
    const cart_id = useAPIState(s => s.cart_id)
    const cart_access_token = useAPIState(s => s.cart_access_token)

    const createCart = usePostCart()
    const updateCart = useMutationCart()
    const hasCart = !!(cart_id && cart_access_token)

    return useMutation({
        mutationKey: ["mutateCart", { id: cart_id }],
        mutationFn: (body: CartBody) => {
            if (!hasCart) {
                return createCart.mutateAsync(body)
            }

            return updateCart.mutateAsync(body)
        },
    })
}

const usePostCartCoupon = () => {
    const queryClient = useQueryClient()
    const { language } = useLocalesState()
    const client = useSDK()
    const cart_id = useAPIState(s => s.cart_id)
    const cart_access_token = useAPIState(s => s.cart_access_token)
    const createCart = usePostCart()

    return useMutation({
        mutationKey: ["mutateCart", { id: cart_id }],
        mutationFn: async (body: CartVoucherBody) => {
            try {
                return await client.updateCartVoucher(
                    cart_id as string,
                    body,
                    { access_token: cart_access_token },
                    language
                )
            } catch (err) {
                const error = err as APIError
                if (error.code !== "989e2d90" && error.code !== "b3101604") throw err

                queryClient.removeQueries({ queryKey: ["getCart", { id: cart_id }] })
                client.clearCart()
                return await createCart.mutateAsync({ voucher: body.code })
            }
        },
        onSuccess: data => {
            const prevData = queryClient.getQueryData<CartResponse>(["getCart", { id: cart_id, language }])
            data.items.forEach(i => {
                eeAddToCart(i, i.quantity, i.price.currency.name, data)
            })
            queryClient.setQueryData(["getCart", { id: cart_id, language }], data)
            if (prevData?.price.total !== data.price.total) {
                queryClient.invalidateQueries({ queryKey: ["getCartFinanceProviders"] })
            }
        },
        retry: (failureCount, error) => {
            return error.type !== "ValidationError" &&
                error.type !== "AccessError" &&
                error.type !== "MissingError" &&
                failureCount <= 2 &&
                error.status !== 404
                ? true
                : false
        },
        retryDelay: attempt => Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000),
    })
}

const usePostCartCouponTrk = () => {
    const queryClient = useQueryClient()
    const { language } = useLocalesState()
    const client = useSDK()
    const cart_id = useAPIState(s => s.cart_id)
    const cart_access_token = useAPIState(s => s.cart_access_token)
    const createCart = usePostCart()

    return useMutation({
        mutationKey: ["mutateCart", { id: cart_id }],
        mutationFn: async (body: CartVoucherBody) => {
            try {
                return await client.updateCartVoucherTrk(
                    cart_id as string,
                    body,
                    { access_token: cart_access_token },
                    language
                )
            } catch (err) {
                const error = err as APIError
                if (error.code !== "989e2d90" && error.code !== "b3101604") throw err

                queryClient.removeQueries({ queryKey: ["getCart", { id: cart_id }] })
                client.clearCart()
                return await createCart.mutateAsync({ voucher_trk: body.code })
            }
        },
        onSuccess: data => {
            const prevData = queryClient.getQueryData<CartResponse>(["getCart", { id: cart_id, language }])
            data.items.forEach(i => {
                eeAddToCart(i, i.quantity, i.price.currency.name, data)
            })
            queryClient.setQueryData(["getCart", { id: cart_id, language }], data)
            if (prevData?.price.total !== data.price.total) {
                queryClient.invalidateQueries({ queryKey: ["getCartFinanceProviders"] })
            }
        },
        retry: (failureCount, error) => {
            return error.type !== "ValidationError" &&
                error.type !== "AccessError" &&
                error.type !== "MissingError" &&
                failureCount <= 2 &&
                error.status !== 404
                ? true
                : false
        },
        retryDelay: attempt => Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000),
    })
}

const useCartCoupon = () => {
    const cart_id = useAPIState(s => s.cart_id)
    const cart_access_token = useAPIState(s => s.cart_access_token)

    const createCart = usePostCart()
    const createCartCoupon = usePostCartCoupon()
    const hasCart = !!(cart_id && cart_access_token)

    return useMutation({
        mutationKey: ["mutateCart", { id: cart_id }],
        mutationFn: (body: CartVoucherBody) => {
            if (!hasCart) {
                return createCart.mutateAsync({ voucher: body.code })
            }

            return createCartCoupon.mutateAsync(body)
        },
    })
}

const useCartCouponTrk = () => {
    const cart_id = useAPIState(s => s.cart_id)
    const cart_access_token = useAPIState(s => s.cart_access_token)

    const createCart = usePostCart()
    const createCartCoupon = usePostCartCouponTrk()
    const hasCart = !!(cart_id && cart_access_token)

    return useMutation({
        mutationKey: ["mutateCart", { id: cart_id }],
        mutationFn: async (body: CartVoucherBody) => {
            if (!hasCart) {
                await createCart.mutateAsync({ voucher_trk: body.code })
                return createCartCoupon.mutateAsync(body)
            }

            return createCartCoupon.mutateAsync(body)
        },
    })
}

export {
    useQueryCart,
    useQueryHistoricCart,
    usePostCart,
    useMutationCart,
    useMutationCartLine,
    useDeleteCartLine,
    useCartLine,
    useCart,
    useCartCoupon,
    useCartCouponTrk,
}
